import { continueRender, delayRender, Sequence, AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { SelectionOutline } from '../../Outlines/SelectionOutline';
import { SelectionElementType } from '../../Outlines/types';
import { CompositionConfig } from '../../../MainPlayer/types/CompositionConfig';
import { TextElement } from '@/App/remotion/MainPlayer/types/Element/TextElement';
import { getTransitionPageInStyles } from '@/App/remotion/libs/transitions/transitionPageIn';
import { getTransitionWordInStyles } from '@/App/remotion/libs/transitions/transitionWordIn';
import { TransitionInEffect } from '@/App/remotion/libs/transitions/types';
import { PositionStyleProperties } from '@/App/remotion/MainPlayer/types/Style/Position';
import { calculateDynamicBackground } from '@/Pages/Clip/ClipPlayer/ClipAssets/CalloutCaptions/textUtils';
import { loadRemotionFont } from '@/libs/fonts';

interface TextElementComponentProps {
  element: TextElement;
  onOutlineRelease?: CompositionConfig['onOutlineRelease'];
  canEditText?: boolean;
}

const TextElementComponent: React.FC<TextElementComponentProps> = ({
  element,
  onOutlineRelease,
  canEditText = false
}) => {
  const { fromFrame, toFrame, details, containerStyle, transitions } = element;
  const { font } = details;

  // Remotion hooks
  const frame = useCurrentFrame();
  const { fps, width: compositionWidth } = useVideoConfig();

  // Font loading
  const [handle] = useState(() => delayRender('Loading Assets...', { retries: 3 }));

  // Position management
  const textElementRef = useRef<HTMLDivElement>(null);
  const [newPositions, setNewPositions] = useState<PositionStyleProperties>({
    top: containerStyle.top,
    left: containerStyle.left,
    width: containerStyle.width,
    height: containerStyle.height
  });
  const [dynamicStyle, setDynamicStyle] = useState(element.style);

  useEffect(() => {
    if (font?.location && font.name) {
      loadRemotionFont(font)
        .then(() => {
          // Font loaded successfully
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('Error loading font:', font.name, font.location, error);
        })
        .finally(() => {
          continueRender(handle);
        });
    } else {
      continueRender(handle);
    }
  }, [font?.location, font?.name, handle]);

  // Single unified function for dynamic height calculation
  const updateDynamicHeight = useCallback(
    (positions?: PositionStyleProperties) => {
      if (!textElementRef.current) {
        return null;
      }

      try {
        // Use provided positions or current positions
        const currentPositions = positions || newPositions;
        const containerStyleWidth = currentPositions.width;

        const widthPercent =
          typeof containerStyleWidth === 'string' && containerStyleWidth.endsWith('%')
            ? parseFloat(containerStyleWidth.replace('%', ''))
            : 60;

        const canvas =
          textElementRef.current.closest('canvas') || textElementRef.current.closest('[data-remotion-canvas]');
        const canvasWidth = canvas ? canvas.getBoundingClientRect().width : compositionWidth;
        const containerWidthPx = (widthPercent / 100) * canvasWidth;

        if (containerWidthPx > 0 && dynamicStyle.background !== 'transparent') {
          const newBackground = calculateDynamicBackground(details.text, containerWidthPx, dynamicStyle);

          // Update state immediately for instant visual feedback
          setDynamicStyle(prevStyle => ({
            ...prevStyle,
            background: newBackground
          }));

          // Return updated element for database persistence
          const updatedElement = {
            ...element,
            style: {
              ...element.style,
              background: newBackground
            }
          };

          return updatedElement;
        }
      } catch (error) {
        console.warn('Failed to calculate dynamic height for text element:', error);
      }
      return null;
    },
    [details.text, dynamicStyle, newPositions, element, compositionWidth]
  );

  // Sync dynamic style when element.style changes
  useEffect(() => {
    setDynamicStyle(element.style);
  }, [element.style]);

  // Please do not change this depedencies for linting purposes
  useEffect(() => {
    updateDynamicHeight();
  }, [details.text, dynamicStyle.fontFamily, dynamicStyle.fontSize, dynamicStyle.textTransform]);

  // Position synchronization
  useEffect(() => {
    setNewPositions({
      top: containerStyle.top,
      left: containerStyle.left,
      width: containerStyle.width,
      height: containerStyle.height
    });
  }, [containerStyle.top, containerStyle.left, containerStyle.width, containerStyle.height]);

  // Position update handlers
  const updatePositions = useCallback((positions: PositionStyleProperties) => {
    setNewPositions(prev => ({ ...prev, ...positions }));
  }, []);

  const onSelectionOutlineRelease = useCallback(
    (positions: PositionStyleProperties) => {
      const isPositionsSame =
        positions.top === newPositions.top &&
        positions.left === newPositions.left &&
        positions.width === newPositions.width &&
        positions.height === newPositions.height;

      if (isPositionsSame) {
        return;
      }

      setNewPositions(positions);

      // Update dynamic height and get updated element
      const updatedElement = updateDynamicHeight(positions);

      onOutlineRelease?.(positions, updatedElement || element);
    },
    [onOutlineRelease, newPositions, element, updateDynamicHeight]
  );

  // Page-level animation styles (same as captions)
  const pageAnimationStyles = useMemo(() => {
    if (!transitions?.pageIn) {
      return {};
    }

    return getTransitionPageInStyles({
      fps,
      frame: frame - fromFrame, // Relative to element start
      animationType: transitions.pageIn
    });
  }, [transitions.pageIn, fps, frame, fromFrame]);

  const durationInFrames = Math.round(toFrame - fromFrame);
  const inheritedStyles = useMemo(() => {
    return {
      fontFamily: 'inherit',
      lineHeight: 'inherit'
    };
  }, []);

  return (
    <Sequence from={fromFrame} durationInFrames={durationInFrames} layout="none">
      <AbsoluteFill
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          ref={textElementRef}
          style={{
            ...containerStyle,
            ...newPositions,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              ...pageAnimationStyles,
              ...dynamicStyle,
              display: 'inline-block',
              position: 'static',
              width: 'fit-content',
              maxWidth: '100%',
              height: 'auto'
            }}
          >
            {transitions?.wordIn && transitions.wordIn !== TransitionInEffect.None ? (
              <div style={{ ...inheritedStyles, textWrap: 'balance' }}>
                {details.text.split(/\s+/).map((word, index, arr) => {
                  const wordAnimationStyles = getTransitionWordInStyles({
                    fps,
                    frame: frame - fromFrame,
                    startFrame: index * 3,
                    animationType: transitions.wordIn || TransitionInEffect.None
                  });
                  return (
                    <span
                      key={index}
                      style={{
                        ...wordAnimationStyles,
                        display: 'inline-block',
                        ...inheritedStyles
                      }}
                    >
                      {word}
                      {index !== arr.length - 1 && '\u00A0'}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div style={{ ...inheritedStyles, textWrap: 'balance' }}>{details.text}</div>
            )}
          </div>
        </div>

        {/* Selection Outline for Editing */}
        {canEditText && (
          <SelectionOutline
            positions={newPositions}
            updatePositions={updatePositions}
            onOutlineRelease={onSelectionOutlineRelease}
            allowResize={true}
            elementType={SelectionElementType.TEXT}
            data={{ elementId: element.id, type: SelectionElementType.TEXT }}
          />
        )}
      </AbsoluteFill>
    </Sequence>
  );
};

export default TextElementComponent;
